import { ColorTypes } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Control, useForm } from 'react-hook-form';

import { RedemptionFormData, getValidationCreateSchema } from './constants';

export const useDirectTreasuryRedemptionController = (
  minValue: number,
  maxValue: number,
  defaultQuantity: number,
  unitPrice: number
) => {
  const [prevValue, setPrevValue] = useState(0);
  const [valueApplied, setValueApplied] = useState(minValue);
  const [amountItem, setAmountItem] = useState(defaultQuantity);
  const [isFocused, setIsFocused] = useState(false);

  const [showMessageAdjustUnitPrice, setShowMessageAdjustUnitPrice] = useState(false);

  const tableForm = useForm<RedemptionFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      redemptionValue: Utils.Mask.Money(minValue),
    },
    resolver: zodResolver(getValidationCreateSchema(minValue, maxValue)),
  });

  const getRedemptionValue = () => {
    const currentValue = tableForm.getValues('redemptionValue');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    const isMultipleBalance = currentValueUnformatted % unitPrice === 0;

    const quotient = parseInt((currentValueUnformatted / unitPrice).toFixed(0), 10);
    const multipliedValue = quotient * parseFloat(unitPrice.toFixed(2));
    const redemptionValue = parseFloat(multipliedValue.toFixed(2));

    return { redemptionValue, isMultipleBalance, quotient };
  };

  const getRedemptionValueOrMinimum = () => {
    const { redemptionValue } = getRedemptionValue();
    return redemptionValue > minValue ? redemptionValue : minValue;
  };

  const isAbleToChange = (value: number) => {
    return !!(
      minValue &&
      maxValue &&
      prevValue !== value &&
      value >= minValue &&
      value <= maxValue
    );
  };

  const handleSubmit = () => {
    const redemptionValue = getRedemptionValueOrMinimum();

    tableForm.setValue('redemptionValue', Utils.Mask.Money(redemptionValue));
    setValueApplied(redemptionValue);

    setIsFocused(false);
  };

  const handleChangeAmountItem = (value: number) => {
    const multipliedValue = value * parseFloat(unitPrice.toFixed(2));
    const redemptionValue = parseFloat(multipliedValue.toFixed(2));

    setAmountItem(value);
    tableForm.setValue('redemptionValue', Utils.Mask.Money(redemptionValue));
    setValueApplied(redemptionValue);
  };

  const handleInputBlur = async () => {
    const { isMultipleBalance, quotient, redemptionValue } = getRedemptionValue();
    const redemptionValueFormatted = Utils.Mask.Money(redemptionValue);

    if (redemptionValue !== minValue) {
      if (isAbleToChange(redemptionValue)) {
        tableForm.setValue('redemptionValue', redemptionValueFormatted);
        setValueApplied(redemptionValue);
        setPrevValue(redemptionValue);
        setAmountItem(quotient / 100);
      }
      if (!isMultipleBalance) {
        setShowMessageAdjustUnitPrice(true);
      }
    }

    if (isFocused) {
      setIsFocused(false);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setPrevValue(minValue);
    setShowMessageAdjustUnitPrice(false);
    if (tableForm.formState.errors?.redemptionValue) {
      tableForm.resetField('redemptionValue');
    }
  };

  const handleInputClear = () => {
    setIsFocused(false);
    tableForm.resetField('redemptionValue');
    tableForm.clearErrors('redemptionValue');
  };

  const helpTextWhenIsFocused = showMessageAdjustUnitPrice
    ? 'Ajustado por preço unitário'
    : `Mínimo: ${Utils.Mask.Money(minValue)}`;

  const messageAdjustUnitPriceColor = {
    color: (isFocused ? 'brand.tertiary.principal' : 'neutral.light') as ColorTypes.Option,
  };

  useEffect(() => {
    setPrevValue(0);
    setValueApplied(minValue);
    setIsFocused(false);
    setAmountItem(defaultQuantity);
  }, [minValue, maxValue, defaultQuantity]);

  return {
    isFocused,
    valueApplied,
    valueAppliedFormatted: Utils.Mask.Money(valueApplied),
    handleInputBlur,
    handleInputFocus,
    handleInputClear,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    hasError: tableForm.formState.errors?.redemptionValue,
    helpTextProps: messageAdjustUnitPriceColor,
    helpText: helpTextWhenIsFocused,
    handleChangeAmountItem,
    amountItem,
    showMessageAdjustUnitPrice,
  };
};
